import { useState } from "react"
import { Camera01, Copy01, Image01, MessageTextSquare01, Sun, Phone01 } from "@untitledui/icons"
import type { DamageZone } from "../types"
import { FlowCta, FlowHeading, PhasePane, QuietLink, StickyActions } from "../flow-chrome"
import { CAPTURE_COPY } from "./capture-copy"
import { StandMap } from "./stand-map"
import type { PlannedShot } from "./types"

// Sets expectations before the camera opens: how many photos, how long,
// where the walk starts. Knowing "7 photos, 2 minutes" is what lets a
// stressed person relax into it. Doubles as the camera-permission primer.

const C = CAPTURE_COPY.brief

export function CaptureBrief({
  plan,
  damage,
  onOpen,
  onGallery,
  onLater,
  laterBusy,
  liveCamera,
  inAppBrowser,
}: {
  plan: readonly PlannedShot[]
  damage: readonly DamageZone[]
  onOpen: () => void
  onGallery: (() => void) | null
  onLater: (() => void) | null
  laterBusy: boolean
  // The live camera will coach them; the phone's own camera can't.
  liveCamera: boolean
  // Opened inside Facebook / Instagram / TikTok…: suggest the real browser.
  inAppBrowser: boolean
}) {
  // Where to stand first: the first corner (the VIN step, when there is
  // one, comes before it, at the driver's door).
  const first = plan.find((s) => s.kind === "wide") ?? plan[0]
  const vinFirst = plan[0]?.kind === "sticker"
  const [copied, setCopied] = useState(false)
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }
  return (
    <PhasePane className="pt-2">
      <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">
        {C.title(plan.length)}
      </FlowHeading>
      <p className="mt-2 text-[15px] leading-relaxed text-zinc-600">{C.sub}</p>

      {first && (
        <figure className="mt-5 flex items-center gap-4 rounded-3xl bg-white p-4 ring-1 ring-ink/8">
          <div className="w-[104px] shrink-0">
            <StandMap shot={first} plan={plan} damage={damage} label="Where to stand first" />
          </div>
          <figcaption className="text-[15px] font-semibold leading-snug text-ink">
            {first.corner && (vinFirst ? C.startAtVin(first.corner) : C.startAt(first.corner))}
          </figcaption>
        </figure>
      )}

      <ul className="mt-5 space-y-3">
        <li className="flex gap-3 text-[15px] leading-snug text-zinc-700">
          <Sun className="mt-0.5 size-5 shrink-0 text-zinc-500" aria-hidden />
          {C.tips[0]}
        </li>
        <li className="flex gap-3 text-[15px] leading-snug text-zinc-700">
          <Phone01 className="mt-0.5 size-5 shrink-0 text-zinc-500" aria-hidden />
          {liveCamera ? C.tipLive : C.tips[1]}
        </li>
      </ul>

      {inAppBrowser && (
        <div className="mt-5 rounded-2xl bg-white p-4 ring-1 ring-ink/8">
          <p className="text-[15px] font-semibold text-ink">{C.inApp.title}</p>
          <p className="mt-1 text-[14px] leading-snug text-zinc-600">{C.inApp.body}</p>
          <button
            type="button"
            onClick={() => void copyLink()}
            className="mt-3 inline-flex h-11 items-center gap-2 rounded-xl border border-zinc-300 bg-white px-4 text-[14px] font-semibold text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <Copy01 className="size-4" aria-hidden />
            {copied ? C.inApp.copied : C.inApp.copy}
          </button>
        </div>
      )}

      <StickyActions>
        <FlowCta onClick={onOpen}>
          <Camera01 className="size-5" aria-hidden />
          {C.open}
        </FlowCta>
        <p className="mt-2 text-center text-[13px] text-zinc-500">{C.permission}</p>
        {onGallery && (
          <QuietLink onClick={onGallery} className="mt-2">
            <Image01 className="size-4" aria-hidden />
            {C.gallery}
          </QuietLink>
        )}
        {onLater && (
          <QuietLink onClick={onLater} disabled={laterBusy}>
            <MessageTextSquare01 className="size-4" aria-hidden />
            {C.later}
          </QuietLink>
        )}
      </StickyActions>
    </PhasePane>
  )
}
